"use server";

import { randomInt } from "node:crypto";
import { z } from "zod";
import { requireMember } from "@/lib/guard";
import { hashPassword } from "@/lib/password";
import { clearFails } from "@/lib/rate-limit";
import { deleteMemberData, getMember, resetMemberPassword } from "@/lib/store";

const memberIdSchema = z.string().uuid();

/**
 * 임시 비밀번호에 쓰는 글자. 사람이 불러주고 받아 적는 값이라 헷갈리는 짝
 * (0/O, 1/l/I)은 뺐다.
 */
const TEMPORARY_ALPHABET = "abcdefghjkmnpqrstuvwxyz23456789";
const TEMPORARY_LENGTH = 10;

/**
 * 한 번 쓰고 버리는 임시 비밀번호를 만든다.
 *
 * 예전에는 모두에게 같은 "0000" 을 줬다. 값이 공개돼 있으니 초기화된 계정은
 * 누구나 먼저 들어갈 수 있었고, 바꾸라고 강제하지도 않아 그대로 남았다.
 */
function temporaryPassword(): string {
  let out = "";
  for (let i = 0; i < TEMPORARY_LENGTH; i++) {
    out += TEMPORARY_ALPHABET[randomInt(TEMPORARY_ALPHABET.length)];
  }
  return out;
}

export type ResetPasswordResult =
  | { ok: true; temporaryPassword: string }
  | { ok: false; error: string };

/**
 * 관리자가 회원 비밀번호를 임시 비밀번호로 초기화한다.
 *
 * 만든 값은 이 응답에서 한 번만 돌려준다 — 저장된 건 해시뿐이라 다시 볼 수 없다.
 * 회원은 이 비밀번호로 로그인한 뒤 새 비밀번호를 정해야 앱을 쓸 수 있다.
 */
export async function resetPasswordToDefault(
  rawMemberId: string,
): Promise<ResetPasswordResult> {
  const me = await requireMember();
  if (me.role !== "admin") {
    return { ok: false, error: "관리자만 비밀번호를 초기화할 수 있어요." };
  }

  const memberId = memberIdSchema.safeParse(rawMemberId);
  if (!memberId.success) return { ok: false, error: "회원 정보가 올바르지 않아요." };
  if (memberId.data === me.memberId) {
    return { ok: false, error: "내 비밀번호는 내 정보에서 변경해 주세요." };
  }

  const member = await getMember(memberId.data);
  if (!member?.isActive) return { ok: false, error: "회원을 찾을 수 없어요." };

  const password = temporaryPassword();
  const reset = await resetMemberPassword(member.id, await hashPassword(password));
  if (!reset) return { ok: false, error: "비밀번호를 초기화하지 못했어요." };

  clearFails(member.loginId);
  return { ok: true, temporaryPassword: password };
}

export async function deleteMemberAccount(
  rawMemberId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const me = await requireMember();
  if (me.role !== "admin") {
    return { ok: false, error: "관리자만 회원 계정을 삭제할 수 있어요." };
  }

  const memberId = memberIdSchema.safeParse(rawMemberId);
  if (!memberId.success) return { ok: false, error: "회원 정보가 올바르지 않아요." };
  if (memberId.data === me.memberId) {
    return { ok: false, error: "현재 로그인한 관리자 계정은 삭제할 수 없어요." };
  }

  const member = await getMember(memberId.data);
  if (!member?.isActive) return { ok: false, error: "회원을 찾을 수 없어요." };

  const deleted = await deleteMemberData(member.id);
  if (!deleted.ok) {
    return { ok: false, error: "마지막 관리자 계정은 삭제할 수 없어요." };
  }

  clearFails(member.loginId);
  return { ok: true };
}
